
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { isNil, debounce } from 'lodash';

import TextField from '../../../../../../framework/fields/TextField.vue';
import SelectField from '../../../../../../framework/fields/SelectField.vue';
import AutoCompleteField from '../../../../../../framework/fields/AutoCompleteField.vue';
import CurrencyAmountField from '../../../../../../framework/fields/CurrencyAmountField.vue';
import NumberField from '../../../../../../framework/fields/NumberField.vue';
import PercentageField from '../../../../../../framework/fields/PercentageField.vue';
import DateField from '../../../../../../framework/fields/DateField.vue';
import { ValidationObserver } from 'vee-validate';
import { sharedState } from '../../../../../../framework/state';
import {
    deleteResource,
    getResource,
    getResourcePagingNew,
    getResourcesNew,
    putResource,
} from '../../../../../../framework/client/resource';
import { getDatabaseOptions, getEnumerationOptions, getOptionLabel } from '../../../../../../framework/service/options';
import { dateToDbDateString } from '../../../../../../../common/framework/util/convert';
import { getResourceLabels, getResourceLabelsForFields } from '../../../../../../framework/service/label_service';

import VendorContractSelect from '../../../../../fields/VendorContractSelect.vue';
import AutoCompleteMultipleField from '../../../../../fields/AutoCompleteMultipleField.vue';
import { PageSize } from '../../../../../../../common/application/model/enums/PageSize';
import {
    ExtendedPurchaseInvoiceRow,
    PurchaseInvoiceRow,
} from '../../../../../../../common/application/model/PurchaseInvoiceRow';
import { ApplicationResource } from '../../../../../../../common/application/enumeration/ApplicationResource';
import { getPurchaseInvoiceMapping } from '../../../../../client/purchase_invoice_mapping_client';
import { PurchaseInvoiceRowMapping } from '../../../../../../../common/application/model/PurchaseInvoiceRowMapping';
import { VendorContractType } from '../../../../../../../common/application/model/enums/VendorContractType';
import { VehicleState } from '../../../../../../../common/application/model/enums/VehicleState';
import { PurchaseInvoice } from '../../../../../../../common/application/model/PurchaseInvoice';
import { errorToast, successToast } from '../../../../../service/toast_service';
import { ApplicationUserRole } from '../../../../../../../common/application/enumeration/ApplicationUserRole';
import {
    getFinvoiceRowSuggestionsByInvoiceId,
    putUpdateAllInvoiceRowsTypeReq,
    updatePurchaseInvoiceRow,
} from '../../../../../client/purchase_invoice_client';
import {
    getPurchaseInvoiceRowActionReason,
    PurchaseInvoiceRowActionReason,
} from '../../../../../../../common/application/logic/purchase_invoice_row_action';
import { RowRegNumberContracts } from '../../../../../../../common/application/model/api/RowRegNumberContracts';

import { formatCurrency, searchAblePurchaseInvoiceRowsColumns } from '../../../../../service/helper_service';
import ParameterService from '../../../../../service/parameter_service';
import { UpdateAllInvoiceRowsTypeReq } from '../../../../../../../common/application/model/api/UpdateAllInvoiceRowsTypeReq';
import { postGenerateInvoiceRowsByTextract } from '../../../../../client/textract_client';
import QuickAddVendorContractModal from '../../../contract/vendor/QuickAddVendorContractModal.vue';
import { VendorContractPrefillResponse } from '../../../../../../../common/application/model/api/VendorContractPrefillResponse';

@Component({
    components: {
        SelectField,
        TextField,
        AutoCompleteField,
        VendorContractSelect,
        CurrencyAmountField,
        PercentageField,
        NumberField,
        DateField,
        AutoCompleteMultipleField,
        ValidationObserver,
        QuickAddVendorContractModal,
    },
})
export default class PurchaseInvoiceRows extends Vue {
    readonly resourceType = ApplicationResource.PURCHASE_INVOICE_ROW;
    parameterService = new ParameterService(this.resourceType);

    @Prop(String) readonly purchaseInvoiceId!: string;
    @Prop(String) readonly vendorName!: string;

    shared = sharedState;
    local = {
        rows: [] as ExtendedPurchaseInvoiceRow[],
        pageSizeOptions: [] as number[],
        vehicleContractLabels: new Map<string, string>(),
        vendorContractLabels: new Map<string, string>(),
        vehicleContractParams: new Map<string, any>(),
        total: 0,
        loading: false,
        parameters: this.parameterService.get(),
        selected: undefined as ExtendedPurchaseInvoiceRow | undefined,
        checkedRows: [] as ExtendedPurchaseInvoiceRow[],
        invoiceRowTypeOptions: [] as { id: string | undefined; label: string }[],
        vendorContractTypeOptions: [] as { id: string | undefined; label: string }[],
        vehicleStateOptions: [] as { id: string | undefined; label: string }[],
        mappings: new Map<string, PurchaseInvoiceRowMapping | undefined>(),
        editingRow: {} as ExtendedPurchaseInvoiceRow,
        purchaseInvoice: {} as PurchaseInvoice | undefined,
        rowRegNumberMatches: {} as RowRegNumberContracts,
        tableOptions: {
            paginated: true,
        },
        searchAbleColumns: searchAblePurchaseInvoiceRowsColumns,
        showSearchInfo: false,
        search: '' as string | undefined,
        rowMultiSelect: false,
        selectedInvoiceRowType: '' as string,
        isQuickAddModalActive: false,
        quickAddInvoiceRowId: '',
        prefillData: {} as VendorContractPrefillResponse,
    };

    isUpdateAllRowTypesModalActive = false;

    async mounted() {
        this.local.pageSizeOptions = Object.values(PageSize).filter((o) => typeof (o as any) === 'number') as number[];
        this.local.invoiceRowTypeOptions = (await getDatabaseOptions('InvoiceRowType', this)).filter(
            ({ id }) => id !== undefined,
        );
        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );
        this.local.vehicleStateOptions = getEnumerationOptions('VehicleState', Object.keys(VehicleState), this);

        this.setSearchString();
        await this.loadAsyncData();
    }

    @Watch('local.parameters.pageSize')
    async pageSizeChange() {
        if (this.local.loading) {
            return;
        }

        await this.resetPagingAndLoadData();
    }

    @Watch('local.rowMultiSelect')
    async onRowMultiSelectChange() {
        this.local.selected = undefined;
        this.local.checkedRows = [];
    }

    @Watch('local.search')
    searchChange(searchString: string) {
        if (this.local.loading) {
            return;
        }

        this.deBounceSearch(searchString);
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    private deBounceSearch = debounce((searchString: string) => {
        this.setSearch(searchString);
    }, 1200);

    add() {
        this.$router.push(`/${ApplicationResource.PURCHASE_INVOICE}/${this.purchaseInvoiceId}/add-row`);
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    async addMapping(purchaseInvoiceRow: ExtendedPurchaseInvoiceRow) {
        const purchaseInvoice = await getResource<PurchaseInvoice>(
            ApplicationResource.PURCHASE_INVOICE,
            purchaseInvoiceRow.purchaseInvoiceId,
        );

        await this.$router.push(
            `/${ApplicationResource.PURCHASE_INVOICE_ROW_MAPPING}/add?vendorType=` +
                purchaseInvoice!!.vendorType +
                '&vendorProductPattern=' +
                (purchaseInvoiceRow.description ? purchaseInvoiceRow.description : purchaseInvoiceRow.productName),
        );
    }

    view() {
        if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}`);
        }
    }

    split() {
        if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/split`);
        }
    }

    changePeriod() {
        if (!this.local.selected) {
            this.$router.push(`/${ApplicationResource.PURCHASE_INVOICE}/${this.purchaseInvoiceId}/change-period`);
        }
    }

    distribute() {
        this.$router.push(
            '/purchase-invoice/' +
                this.purchaseInvoiceId +
                '/distribute?purchaseInvoiceRowIds=' +
                (!isNil(this.local.selected)
                    ? this.local.selected.id
                    : this.local.checkedRows.map((r) => r.id).join(',')),
        );
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    dateToString(date: string | undefined): string {
        if (!date) {
            return '';
        }

        return dateToDbDateString(new Date(date));
    }

    getVehicleContractLabel(id: string) {
        if (this.local.vehicleContractLabels.has(id)) {
            return this.local.vehicleContractLabels.get(id);
        } else {
            return id;
        }
    }

    getVendorContractLabel(id: string) {
        if (this.local.vendorContractLabels.has(id)) {
            return this.local.vendorContractLabels.get(id);
        } else {
            return id;
        }
    }

    getMapping(id: string) {
        if (this.local.mappings.has(id)) {
            return this.local.mappings.get(id)!!;
        }
        return undefined;
    }

    onRowClick(row: ExtendedPurchaseInvoiceRow) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    editRow(row: ExtendedPurchaseInvoiceRow) {
        this.local.editingRow = row;
    }

    async saveInlineEditedRow() {
        try {
            if (this.local.editingRow.startDate > this.local.editingRow.endDate) {
                throw new Error('Start date cannot be later than end date.');
            }
            const savedRow = await updatePurchaseInvoiceRow(this.local.editingRow);

            successToast(this, 'message.purchaseInvoiceRowSaved');

            // No need to refresh the contract labels here - the reload below fetches them again.
            this.local.editingRow = {} as ExtendedPurchaseInvoiceRow;
            await this.loadAsyncData();
        } catch (error: any) {
            if (error instanceof Error) {
                errorToast(this, error.message);
            } else {
                errorToast(this, error);
            }
        }
    }

    cancel() {
        this.local.editingRow = {} as ExtendedPurchaseInvoiceRow;
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    rowsHaveMapping(rows: Array<ExtendedPurchaseInvoiceRow>): boolean {
        if (rows.length === 0) {
            return false;
        }
        return rows.map((r) => !isNil(this.local.mappings.get(r.id))).reduce((a, b) => a && b, true);
    }

    async saveSuggestedContracts(): Promise<void> {
        try {
            for (const row of this.local.rows) {
                if (!this.local.rowRegNumberMatches[row.rowIndex]) {
                    continue;
                }

                const vehicleContract = this.local.rowRegNumberMatches[row.rowIndex].vehicleContracts[0];
                const vendorContract = this.local.rowRegNumberMatches[row.rowIndex].vendorContracts[
                    this.vendorName
                ].find((vendorContract) => {
                    if (!row.mapping) {
                        return undefined;
                    }
                    return vendorContract.vendorContractType === row.mapping.vendorContractType;
                });

                await putResource<PurchaseInvoiceRow>(this.resourceType, row.id, {
                    ...row,
                    invoiceRowType: row.mapping ? row.mapping.invoiceRowType : row.invoiceRowType,
                    vehicleRegistrationNumber:
                        (vehicleContract ? vehicleContract.vehicleRegistrationNumber : row.vehicleRegistrationNumber) ??
                        '',
                    vehicleContractId: vehicleContract ? vehicleContract.id : row.vehicleContractId,
                    vendorContractId: vendorContract ? vendorContract.id : row.vendorContractId,
                });
            }

            await this.loadAsyncData();
        } catch (e) {
            console.error('error saving PurchaseInvoiceRow', e);
            errorToast(this);
        }
    }

    async generateRowsByTextract() {
        try {
            this.$buefy.snackbar.open({
                message: this.$t('message.processingInvoiceRowsGeneration').toString(),
                type: 'is-info',
                position: 'is-top',
                indefinite: true,
            });

            await postGenerateInvoiceRowsByTextract(this.purchaseInvoiceId);

            this.$buefy.snackbar.open({
                message: this.$t('message.invoiceRowsProcessingFinished').toString(),
                type: 'is-accepted',
                position: 'is-top',
                indefinite: true,
                actionText: this.$t('button.reload').toString(),
                onAction: () => {
                    window.location.reload();
                },
            });
        } catch (error) {
            errorToast(this);
        }
    }

    getVendorContractTypeType(id: string): string {
        return getOptionLabel(id, this.local.vendorContractTypeOptions);
    }

    rowRegistrationNumber(rowIndex: string): string {
        if (!this.local.rowRegNumberMatches[rowIndex]) {
            return '';
        }

        return (
            (this.local.rowRegNumberMatches[rowIndex].vehicleContracts[0]
                ? this.local.rowRegNumberMatches[rowIndex].vehicleContracts[0].vehicleRegistrationNumber
                : '') ?? ''
        );
    }

    async dropdownClosed(resourceIds: string[], idField: 'customerId' | 'id'): Promise<void> {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        await this.resetPagingAndLoadData();
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    /**
     * Built once per load and stored in `local`, never called from the template.
     *
     * AutoCompleteMultipleField watches its `parameters` prop, and a freshly constructed Map is
     * never `===` the previous one - so building this during render made every re-render refetch
     * the vehicle contract list. Combined with the hover handlers that re-rendered the whole table
     * on mouse move, that turned moving the mouse into a stream of requests.
     */
    private buildVehicleContractParams(): Map<string, any> {
        const params = new Map<string, any>();
        const invoiceVehicleContractIds: string[] = [];
        this.local.rows.forEach((row) => {
            if (row.vehicleContractId) {
                invoiceVehicleContractIds.push(row.vehicleContractId);
            }
        });

        if (invoiceVehicleContractIds.length) {
            params.set('id', invoiceVehicleContractIds);
        }

        return params;
    }

    rowClass(row: ExtendedPurchaseInvoiceRow) {
        return row.purchaseInvoiceRowActionRequired ? 'has-background-danger-light' : '';
    }

    /**
     * What to print on a red row: why it needs attention, not just that it does.
     *
     * A vehicle that is inactive or in realization is normally the reason no vendor contract could
     * be resolved, and such a row otherwise looks like any other - naming the state is the only
     * thing that explains it.
     */
    actionRequiredLabel(row: ExtendedPurchaseInvoiceRow): string {
        return row.purchaseInvoiceRowActionVehicleState
            ? getOptionLabel(row.purchaseInvoiceRowActionVehicleState, this.local.vehicleStateOptions)
            : String(this.$t('message.purchaseInvoiceRowActionRequired'));
    }

    // A method reference rather than an inline arrow in the template, so the prop keeps its
    // identity between renders.
    isRowSelectable(): boolean {
        return this.local.editingRow.id === undefined && !this.local.parameters.rowMultiSelect;
    }

    async openQuickAdd(purchaseInvoiceRowId: string, vehicleContractId: string, invoiceRowType: string) {
        if (!vehicleContractId) {
            errorToast(this, 'message.missingVehicleContract');
            return;
        }

        if (!invoiceRowType) {
            errorToast(this, 'message.missingInvoiceRowType');
            return;
        }
        await updatePurchaseInvoiceRow(this.local.editingRow);
        this.local.quickAddInvoiceRowId = purchaseInvoiceRowId;
        this.local.isQuickAddModalActive = true;
    }

    refresh() {
        this.local.isQuickAddModalActive = false;
        this.local.quickAddInvoiceRowId = '';
        this.cancel();
        this.loadAsyncData();
    }

    async updateAllInvoiceRowTypes() {
        if (!this.isAdmin) {
            return;
        }

        const updateAllInvoiceRowsTypeReq: UpdateAllInvoiceRowsTypeReq = {
            purchaseInvoiceId: this.purchaseInvoiceId,
            invoiceRowType: String(this.local.selectedInvoiceRowType),
        };

        try {
            this.local.loading = true;
            await putUpdateAllInvoiceRowsTypeReq(updateAllInvoiceRowsTypeReq);
            this.isUpdateAllRowTypesModalActive = false;
            this.local.loading = false;
            successToast(this, 'message.allInvoiceRowTypesUpdated');
            await this.loadAsyncData();
        } catch (error) {
            errorToast(error);
        }
    }

    private async loadAsyncData() {
        if (this.local.loading) {
            return;
        }

        const backendParameters = this.parameterService.getBackendParameters();
        const customPart = backendParameters.get('custom');
        backendParameters.delete('custom');

        if (this.purchaseInvoiceId) {
            backendParameters.set('purchaseInvoiceId', this.purchaseInvoiceId);
        }

        this.local.loading = true;

        try {
            // Independent of one another, so fetch them together instead of in a chain. The
            // /purchase-invoice-row route renders this component without an invoice, so everything
            // scoped to one invoice is skipped rather than requested with an undefined id.
            const [paging, rows, purchaseInvoice] = await Promise.all([
                getResourcePagingNew(this.resourceType, backendParameters, customPart),
                getResourcesNew<PurchaseInvoiceRow>(
                    this.resourceType,
                    this.local.parameters.page,
                    backendParameters,
                    customPart,
                ),
                this.purchaseInvoiceId
                    ? getResource<PurchaseInvoice>(ApplicationResource.PURCHASE_INVOICE, this.purchaseInvoiceId)
                    : undefined,
            ]);

            this.local.total = paging.rowCount;
            this.local.purchaseInvoice = purchaseInvoice;

            // These need the row list, but not each other.
            const [mappingResult, rowRegNumberMatches] = await Promise.all([
                this.purchaseInvoiceId ? getPurchaseInvoiceMapping(this.purchaseInvoiceId) : { mappings: [] },
                this.purchaseInvoiceId
                    ? getFinvoiceRowSuggestionsByInvoiceId(
                          this.purchaseInvoiceId,
                          rows.map(({ rowIndex }) => rowIndex),
                      )
                    : ({} as RowRegNumberContracts),
            ]);

            this.local.mappings = new Map(mappingResult.mappings.map((i) => [i.rowId, i.mapping]));
            this.local.rowRegNumberMatches = rowRegNumberMatches;

            const extendedRows: ExtendedPurchaseInvoiceRow[] = rows.map((row) => {
                const mapping = this.getMapping(row.id);
                const extendedRow: ExtendedPurchaseInvoiceRow = {
                    ...row,
                    mapping,
                    mappingLabel: this.getMappingLabel(mapping),
                };

                const vendorContracts = this.local.rowRegNumberMatches[extendedRow.rowIndex]
                    ? this.local.rowRegNumberMatches[row.rowIndex].vendorContracts[this.vendorName]
                    : [];
                const vehicleContracts = this.local.rowRegNumberMatches[extendedRow.rowIndex]
                    ? this.local.rowRegNumberMatches[extendedRow.rowIndex].vehicleContracts
                    : [];

                let vendorContractId = extendedRow.vendorContractId;
                let vehicleContractId = extendedRow.vehicleContractId;

                if (!vendorContractId && !extendedRow.mapping && vendorContracts?.[0]) {
                    vendorContractId = vendorContracts[0].id;
                }

                if (!vehicleContractId && vehicleContracts?.[0]) {
                    vehicleContractId = vehicleContracts[0].id;
                }

                return {
                    ...extendedRow,
                    vendorContractId,
                    vehicleContractId,
                };
            });

            // Both contract lookups are batched over the whole page. The vehicle contract one reads
            // two fields out of a single request, and replaces what used to be one HTTP request per
            // row for the action-required check. Suggested contract ids are included, so their
            // labels resolve too.
            const [vehicleContractFields, vendorContractLabels] = await Promise.all([
                getResourceLabelsForFields(extendedRows, 'vehicleContractId', ApplicationResource.VEHICLE_CONTRACT, [
                    'vehicleRegistrationNumber',
                    'vehicleState',
                ]),
                getResourceLabels(
                    extendedRows,
                    'vendorContractId',
                    ApplicationResource.VENDOR_CONTRACT,
                    'vehicleRegistrationNumber',
                ),
            ]);

            const vehicleStates = vehicleContractFields.get('vehicleState')!!;
            this.local.vehicleContractLabels = vehicleContractFields.get('vehicleRegistrationNumber')!!;
            this.local.vendorContractLabels = vendorContractLabels;

            this.local.rows = extendedRows.map((extendedRow, index) => {
                // Deliberately computed from what is stored, not from the suggested contracts filled
                // in above: those are unsaved pre-fills, and the database ordering that lifts these
                // rows to the top of page one can only see persisted values.
                const row = rows[index];
                const vehicleState = row.vehicleContractId ? vehicleStates.get(row.vehicleContractId) : undefined;
                const reason = getPurchaseInvoiceRowActionReason(row, vehicleState);

                return {
                    ...extendedRow,
                    purchaseInvoiceRowActionRequired: reason !== undefined,
                    purchaseInvoiceRowActionVehicleState:
                        reason === PurchaseInvoiceRowActionReason.VEHICLE_NOT_IN_USE ? vehicleState : undefined,
                };
            });

            // Action-required rows are ordered first by the database - see the `actionRequired` sort
            // expression registered on the table. Sorting here would only reorder the current page.
            this.local.vehicleContractParams = this.buildVehicleContractParams();

            if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
                this.local.selected = undefined;
            }
        } finally {
            // Without this a failed load left `loading` true and the screen stuck for good.
            this.local.loading = false;
        }

        this.$emit('rowsLoaded');
    }

    private async setSearch(searchString: string | undefined) {
        if (searchString === '') {
            searchString = undefined;
        }
        const searchQuery = `(productName ILIKE {productName} OR description ILIKE {description})`;

        if (this.local.parameters.custom && this.local.parameters.custom.startsWith(searchQuery)) {
            this.local.parameters.custom = this.local.parameters.custom.replace(searchQuery, '');
        } else if (this.local.parameters.custom) {
            this.local.parameters.custom = this.local.parameters.custom.replace(`AND ${searchQuery}`, '');
        }

        delete this.local.parameters.productName;
        delete this.local.parameters.description;

        if (searchString) {
            this.local.parameters.productName = `%${searchString}%`;
            this.local.parameters.description = `%${searchString}%`;

            if (this.local.parameters.custom && this.local.parameters.custom.length) {
                this.local.parameters.custom += ` AND ${searchQuery}`;
            } else {
                this.local.parameters.custom = `${searchQuery}`;
            }
        }

        await this.resetPagingAndLoadData();
    }

    private getMappingLabel(mapping: PurchaseInvoiceRowMapping | undefined) {
        if (!isNil(mapping)) {
            return (
                getOptionLabel(mapping.vendorContractType, this.local.vendorContractTypeOptions) +
                ' / ' +
                getOptionLabel(mapping.invoiceRowType, this.local.invoiceRowTypeOptions)
            );
        } else {
            return undefined;
        }
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    private setSearchString(): void {
        if (this.local.parameters.custom && this.local.parameters.productName) {
            this.local.search = this.local.parameters.productName.toString().replaceAll('%', '');
        } else if (this.local.parameters.custom && this.local.parameters.description) {
            this.local.search = this.local.parameters.description.toString().replaceAll('%', '');
        }
    }
}
